import React from "react";
import { Link } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { ArrowRight, CalendarDays, Inbox, Loader2, Mail, PhoneCall, Sparkles } from "lucide-react";
import { api } from "@/lib/api";
import { useAuth } from "@/lib/auth";
import { useMeta } from "@/hooks/useMeta";
import { fmtDateTime, fmtRelative } from "@/lib/format";
import { StatusBadge } from "@/components/dashboard/StatusBadge";
import { Card, PageHeader } from "@/components/dashboard/Ui";

function Stat({ label, value, hint, to, testId, accent }) {
  const inner = (
    <>
      <p className="text-sm font-medium text-muted-foreground">{label}</p>
      <p className="mt-2 font-heading text-3xl font-semibold" style={{ color: accent }} data-testid={testId}>
        {value}
      </p>
      {hint && <p className="mt-1 text-xs text-muted-foreground">{hint}</p>}
    </>
  );
  return to ? (
    <Link to={to} className="stat-card block">
      {inner}
    </Link>
  ) : (
    <div className="stat-card">{inner}</div>
  );
}

export default function OverviewPage() {
  const { user, isSupervisor } = useAuth();
  const { data: meta } = useMeta();
  const { data, isLoading } = useQuery({ queryKey: ["stats"], queryFn: async () => (await api.get("/dashboard/stats")).data, refetchInterval: 60_000 });

  if (isLoading || !data)
    return (
      <div className="flex justify-center py-20">
        <Loader2 className="h-6 w-6 animate-spin text-primary" />
      </div>
    );

  const s = data.by_status;
  const hour = new Date().getHours();
  const greeting = hour < 12 ? "Good morning" : hour < 18 ? "Good afternoon" : "Good evening";
  const services = Object.entries(data.by_service || {}).sort((a, b) => b[1] - a[1]);
  const maxService = services[0]?.[1] || 1;

  return (
    <div data-testid="overview-page">
      <PageHeader title={`${greeting}, ${user?.name?.split(" ")[0]}`} description={isSupervisor ? "Here is what needs the team's attention today." : "Here is what is on your plate today."} />

      <div className="grid grid-cols-1 gap-4 md:grid-cols-2 xl:grid-cols-4">
        <Stat label="New submissions" value={s.new} hint={`${data.this_week} received in the last 7 days`} to="/dashboard/submissions?status=new" testId="stat-new" accent="#2f5d83" />
        <Stat label="Contacted - awaiting scheduling" value={s.contacted} to="/dashboard/submissions?status=contacted" testId="stat-contacted" accent="#087f8c" />
        <Stat label="Upcoming appointments (7 days)" value={data.upcoming_count} to="/dashboard/schedule" testId="stat-upcoming" accent="#3e9b54" />
        <Stat label="No response" value={s.no_response} hint="Consider a second attempt" to="/dashboard/submissions?status=no_response" testId="stat-no-response" accent="#c2493a" />
      </div>

      {isSupervisor && data.pending_emails > 0 && (
        <Link to="/dashboard/emails" className="mt-4 flex items-center gap-3 rounded-[10px] border border-[hsl(46_55%_80%)] bg-[hsl(46_90%_94%)] px-4 py-3 text-sm text-[hsl(30_70%_25%)] hover:bg-[hsl(46_90%_90%)]" data-testid="pending-emails-banner">
          <Mail className="h-4 w-4" />
          <span>
            <strong>{data.pending_emails}</strong> email{data.pending_emails > 1 ? "s are" : " is"} waiting in the outbox. Add the SMTP password to deliver them.
          </span>
          <ArrowRight className="ms-auto h-4 w-4" />
        </Link>
      )}

      <div className="mt-6 grid gap-6 lg:grid-cols-[1.4fr_1fr]">
        <Card className="min-w-0 overflow-hidden">
          <div className="flex items-center justify-between border-b border-border px-5 py-4">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Inbox className="h-5 w-5 text-primary" /> Latest submissions
            </h2>
            <Link to="/dashboard/submissions" className="text-sm font-semibold text-primary hover:underline" data-testid="overview-view-all-submissions">
              View all
            </Link>
          </div>
          <ul className="divide-y divide-border">
            {data.recent.length === 0 && <li className="px-5 py-8 text-center text-sm text-muted-foreground">No submissions yet.</li>}
            {data.recent.map((r) => (
              <li key={r.id}>
                <Link to={`/dashboard/submissions/${r.id}`} className="table-row-hover flex min-w-0 items-center gap-3 px-4 py-3.5 sm:gap-4 sm:px-5" data-testid="overview-recent-row">
                  <div className="min-w-0 flex-1">
                    <p className="truncate font-semibold text-foreground">{r.beneficiary_name}</p>
                    <p className="truncate text-sm text-muted-foreground">
                      {meta?.services?.[r.service] || r.service}
                      {r.assessment_type ? ` - ${meta?.assessment_types?.[r.assessment_type]}` : ""} - {r.contact_name}
                    </p>
                  </div>
                  <div className="hidden text-end text-xs text-muted-foreground sm:block">
                    <p className="inline-flex items-center gap-1">
                      <PhoneCall className="h-3 w-3" /> {meta?.contact_times?.[r.preferred_contact_time] || "No preference"}
                      {r.preferred_time_specific ? ` ${r.preferred_time_specific}` : ""}
                    </p>
                    <p>{fmtRelative(r.created_at)}</p>
                  </div>
                  <StatusBadge status={r.status} />
                </Link>
              </li>
            ))}
          </ul>
        </Card>

        <div className="min-w-0 space-y-6">
          <Card className="overflow-hidden">
            <div className="flex items-center justify-between border-b border-border px-5 py-4">
              <h2 className="flex items-center gap-2 text-lg font-semibold">
                <CalendarDays className="h-5 w-5 text-[hsl(var(--app-leaf))]" /> Next 7 days
              </h2>
              <Link to="/dashboard/schedule" className="text-sm font-semibold text-primary hover:underline">
                Schedule
              </Link>
            </div>
            <ul className="divide-y divide-border">
              {data.upcoming.length === 0 && <li className="px-5 py-8 text-center text-sm text-muted-foreground">No appointments coming up.</li>}
              {data.upcoming.map((a) => (
                <li key={a.id}>
                  <Link to={`/dashboard/schedule?appointment=${a.id}`} className="table-row-hover flex items-start gap-3 px-5 py-3" data-testid="overview-upcoming-row">
                    <div className="mt-0.5 h-2.5 w-2.5 shrink-0 rounded-full bg-primary" />
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-sm font-semibold text-foreground">{a.beneficiary_name}</p>
                      <p className="text-xs text-muted-foreground">
                        {fmtDateTime(a.start)} - {a.admin_name}
                      </p>
                    </div>
                  </Link>
                </li>
              ))}
            </ul>
          </Card>

          <Card className="p-5">
            <h2 className="flex items-center gap-2 text-lg font-semibold">
              <Sparkles className="h-5 w-5 text-[hsl(var(--app-yellow))]" /> Requests by service
            </h2>
            <ul className="mt-4 space-y-3">
              {services.length === 0 && <li className="text-sm text-muted-foreground">No data yet.</li>}
              {services.map(([k, n]) => (
                <li key={k}>
                  <div className="flex justify-between text-sm">
                    <span className="text-foreground">{meta?.services?.[k] || k}</span>
                    <span className="font-semibold text-muted-foreground">{n}</span>
                  </div>
                  <div className="mt-1 h-1.5 w-full overflow-hidden rounded-full bg-secondary">
                    <div className="h-full rounded-full bg-[hsl(var(--app-teal))]" style={{ width: `${Math.max(6, (n / maxService) * 100)}%`, transition: "width 300ms" }} />
                  </div>
                </li>
              ))}
            </ul>
          </Card>
        </div>
      </div>
    </div>
  );
}
